export class Vec2 {
  constructor(public x: number, public y: number) {}

  static from(x: number, y: number): Vec2 {
    return new Vec2(x, y);
  }

  static fromAngle(angle: number): Vec2 {
    return new Vec2(Math.cos(angle), Math.sin(angle));
  }

  add(v: Vec2): Vec2 {
    return new Vec2(this.x + v.x, this.y + v.y);
  }

  sub(v: Vec2): Vec2 {
    return new Vec2(this.x - v.x, this.y - v.y);
  }

  scale(s: number): Vec2 {
    return new Vec2(this.x * s, this.y * s);
  }

  dot(v: Vec2): number {
    return this.x * v.x + this.y * v.y;
  }

  cross(v: Vec2): number {
    return this.x * v.y - this.y * v.x;
  }

  length(): number {
    return Math.sqrt(this.x * this.x + this.y * this.y);
  }

  lengthSq(): number {
    return this.x * this.x + this.y * this.y;
  }

  normalize(): Vec2 {
    const len = this.length();
    if (len < 1e-10) return new Vec2(0, 0);
    return new Vec2(this.x / len, this.y / len);
  }

  rotate(angle: number): Vec2 {
    const cos = Math.cos(angle);
    const sin = Math.sin(angle);
    return new Vec2(
      this.x * cos - this.y * sin,
      this.x * sin + this.y * cos,
    );
  }

  perpendicular(): Vec2 {
    return new Vec2(-this.y, this.x);
  }

  negate(): Vec2 {
    return new Vec2(-this.x, -this.y);
  }

  distanceTo(v: Vec2): number {
    return this.sub(v).length();
  }

  reflect(normal: Vec2): Vec2 {
    // d - 2(d·n)n
    const dotN = this.dot(normal);
    return this.sub(normal.scale(2 * dotN));
  }

  angle(): number {
    return Math.atan2(this.y, this.x);
  }

  clone(): Vec2 {
    return new Vec2(this.x, this.y);
  }
}

export interface LineSegment {
  start: Vec2;
  end: Vec2;
}

export interface RayLine {
  origin: Vec2;
  direction: Vec2;
}

/**
 * Ray-segment intersection. Returns parametric t along the ray and u along the segment.
 * Intersection point = origin + direction * t
 * Returns null if no intersection or intersection is behind ray (t < 0) or outside segment (u not in [0,1]).
 */
export function raySegmentIntersection(
  ray: RayLine,
  segment: LineSegment,
): { t: number; u: number; point: Vec2 } | null {
  const d = ray.direction;
  const s = segment.end.sub(segment.start);
  const denom = d.cross(s);

  if (Math.abs(denom) < 1e-10) return null; // parallel

  const w = segment.start.sub(ray.origin);
  const t = w.cross(s) / denom;
  const u = w.cross(d) / denom;

  if (t < 1e-6 || u < 0 || u > 1) return null;

  const point = ray.origin.add(d.scale(t));
  return { t, u, point };
}

/** Convert wavelength (in meters) to approximate visible color as CSS string */
export function wavelengthToColor(wavelengthM: number, intensity: number = 1): string {
  const nm = wavelengthM * 1e9;
  let r = 0, g = 0, b = 0;

  if (nm >= 380 && nm < 440) {
    r = -(nm - 440) / (440 - 380);
    b = 1;
  } else if (nm >= 440 && nm < 490) {
    g = (nm - 440) / (490 - 440);
    b = 1;
  } else if (nm >= 490 && nm < 510) {
    g = 1;
    b = -(nm - 510) / (510 - 490);
  } else if (nm >= 510 && nm < 580) {
    r = (nm - 510) / (580 - 510);
    g = 1;
  } else if (nm >= 580 && nm < 645) {
    r = 1;
    g = -(nm - 645) / (645 - 580);
  } else if (nm >= 645 && nm <= 780) {
    r = 1;
  }

  // Intensity falloff at edges of visible spectrum
  let factor: number;
  if (nm >= 380 && nm < 420) {
    factor = 0.3 + 0.7 * (nm - 380) / (420 - 380);
  } else if (nm >= 420 && nm <= 700) {
    factor = 1;
  } else if (nm > 700 && nm <= 780) {
    factor = 0.3 + 0.7 * (780 - nm) / (780 - 700);
  } else {
    factor = 0;
  }

  factor *= intensity;

  // Gamma correction
  const gamma = 0.8;
  r = Math.round(255 * Math.pow(r * factor, gamma));
  g = Math.round(255 * Math.pow(g * factor, gamma));
  b = Math.round(255 * Math.pow(b * factor, gamma));

  return `rgb(${r},${g},${b})`;
}

export function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

export function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}
